import queryString from 'query-string';
import get from 'lodash/get';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';
import pluralize from 'rebook/apps/utils/text/pluralize';
import {
  CATALOG_AUTHOR_BOOK_LIST_SET_DATA,
  CATALOG_AUTHOR_BOOK_LIST_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { ssr, authorBookList: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);
    const locationSearch = get(location, 'search');
    const orderBy = get(queryString.parse(locationSearch), 'o', null) || get(ssr, 'query.o', 'popular');
    const subscriptionId = get(ssr, 'query.s', null);
    const bookType = get(match, 'params.bookType', null) || get(ssr, 'query.t', null) || 'text';

    const { authorSlug } = match.params;

    let newState = {
      authorSlug,
      pageNumber,
      orderBy,
      subscriptionId,
      bookType,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.author) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    dispatch(setData(newState));

    if (!newState.author || newState.author.seo_description === undefined) {
      await dispatch(fetchDetail(match));
    }
    if (!newState.books || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchBookList(newState));
    }
  };
}

export function fetchDetail(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { authorSlug } = match.params;
    const url = `/api/authors/s-${authorSlug}/`;

    return await (
      fetchQuery(url, { headers: ssr.headers })
        .then(({ data }) => {
          dispatch(setData({ author: data }));
        })
        .catch(({ data, resp, error }) => {
          if (resp.status === 404) {
            dispatch(Error404Actions.show());
          }
          else {
            Raven.captureMessage('failed to load author book list due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load author book list due to server error');
          }
        })
    );
  };
}

export function fetchBookList(params = {}) {
  return async(dispatch, getState) => {
    const { ssr, authorBookList } = getState();
    const mergedState = { ...authorBookList, ...params };
    const { author, paginateBy, orderBy, subscriptionId, bookType } = mergedState;
    let pageNumber = mergedState.pageNumber || 1;
    if (!params.pageNumber &&
      (authorBookList.subscriptionId !== mergedState.subscriptionId)
      ||(authorBookList.bookType !== mergedState.bookType)
    ) {
      pageNumber = 1;
    }
    if (!author) {
      return;
    }

    let queryParams = {
      authors: author.id,
      offset: (pageNumber - 1) * paginateBy,
      limit: paginateBy,
      o: orderBy,
      author_weight__gte: 0,
      ...(subscriptionId !== null && { subscription_id__in: subscriptionId }),
      ...(bookType !== null && { type: bookType }),
    };

    const qs = queryString.stringify(queryParams);
    const url = `/api/books/?${qs}`;

    const pendingState = {
      // clear the book list before the fetch
      books: null,
      // optimistically update the UI
      pageNumber,
      orderBy,
      lastPageNumber: 0,
      ...(subscriptionId !== null && { subscriptionId }),
      ...(bookType !== null && { bookType }),
    };

    dispatch(setData(pendingState));

    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(({ data }) => {
          const lastPageNumber = Math.ceil(data.meta.total_count / paginateBy);
          dispatch(setData({
            books: data.objects,
            lastPageNumber: lastPageNumber,
            totalBookCount: data.meta.total_count,
            subscriptionId,
            bookType,
          }));
        })
        .catch(({ data, resp, error }) => {
          Raven.captureMessage('failed to load author books due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load author books due to server error');
        })
    );
  };
}

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { authorBookList, payments } = getState();
    const { author, pageNumber } = authorBookList;
    const standardPrice = payments.prices.standard['1'].full_price;
    const desktopUrl = `https://${BASE_HOST}${author.absolute_url}`;
    const cover206x208 = cropStorageImage(author.photo, 206, 208);
    const maybeGenitiveName = author.genitive_full_name || author.cover_name;
    const isAudio = authorBookList.bookType === 'audio';
    const booksCount = author.active_book_count;

    let countBooksText;

    if (isAudio) {
      countBooksText = `${booksCount} ${pluralize(booksCount, 'аудиокнига', 'аудиокниги', 'аудиокниг')}`;
    }
    else {
      countBooksText = `${booksCount} ${pluralize(booksCount, 'книга', 'книги', 'книг')}`;
    }

    const title = `Список всех ${isAudio ? 'аудиокниг' : 'книг'} автора «${author.cover_name}» по порядку: ` +
      `${countBooksText} автора в электронной библиотеке MyBook.ru. ` +
      `${getPageNumberString(pageNumber).trim()}`;

    const description = `Все ${isAudio ? 'аудиокниги' : 'книги'} автора «${author.cover_name}». ` +
      `Список популярных и новых. Зарегистрируйтесь и ${isAudio ? 'слушайте' : 'читайте'} книги онлайн.`;

    const ogTitle = `${author.cover_name} — читать лучшие книги и новинки онлайн, на iPhone, iPad и Android — MyBook`;
    const ogDescription = `Читать онлайн, на телефоне или планшете книги ${maybeGenitiveName} ` +
                          `всего за ${standardPrice} рублей в месяц. ` +
                          `Лучшие и новые книги ${maybeGenitiveName} всегда под рукой.`;
    const ogTags = [
      { name: 'image', value: cover206x208 },
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'url', value: desktopUrl },
    ];
    dispatch(SeoActions.setData({
      canonicalUrl: `${desktopUrl}books/${isAudio ? 'audio/' : ''}`,
      imageSrc: cover206x208,
      title,
      description,
      ogTags,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { author } = payload;
    dispatch({
      type: CATALOG_AUTHOR_BOOK_LIST_SET_DATA,
      payload,
    });
    // update meta tags every time the detail state is changed
    if (author) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch(SeoActions.resetData());
    dispatch({
      type: CATALOG_AUTHOR_BOOK_LIST_RESET_DATA,
    });
  };
}
